/*
 * Voyado PLP controller — shared logic for [data-voyado-search-page] and
 * [data-voyado-landing-page], attached via window.VoyadoPlp.attach(host, opts).
 * Populates the shell from snippets/plp-voyado-filters.liquid via [data-voyado-*]
 * hooks. Committed facets drive the grid; selecting a facet auto-applies to the
 * grid (debounced, grid-only render) — pending mirrors committed. URL: f.<id>=a|b.
 */
(function () {
  'use strict';

  const PAGE_SIZE = 24;
  const BAR_CHIP_LIMIT = 3; // max quick-filter chips in the bar; rest live in the drawer
  const LG = window.matchMedia('(min-width: 1024px)'); // promo DOM placement differs across this breakpoint

  // URL-state helpers live in voyado-plp-url.js, loaded first in both sections.
  const U = window.VoyadoPlpUrl || {};
  const facetParams = U.facetParams;
  const plpRestoreKey = U.plpRestoreKey;
  const readRestore = U.readRestore;
  const saveRestore = U.saveRestore;
  const readFacetsFromUrl = U.readFacetsFromUrl;

  function Controller(host, opts) {
    this.host = host;
    this.fetch = opts.fetch; // fn(params) → Promise<response>
    this.onAutoCorrect = opts.onAutoCorrect || null; // search only — reflect an applied autocorrection
    this.errorState = opts.errorState || 'error';
    this.committed = readFacetsFromUrl();
    this.pending = clone(this.committed);
    this.sort = new URLSearchParams(location.search).get('sort') || null;
    this.restore = readRestore(plpRestoreKey()); // {count, scroll} on back/forward, else null
    this.loaded = [];
    this.facetsData = [];
    this.facetUniverse = null; // full facet value set (see mergedFacets/seedUniverse)
    this.applyTimer = null; // debounced auto-apply on facet select
    // Generation guard: a stale async response bails when a newer load started.
    this._gen = 0;
    this.q(host);
    this.bind();
    this.load(true);
  }

  Controller.prototype.q = function (host) {
    this.grid = host.querySelector('[data-voyado-grid]');
    this.facetsList = host.querySelector('[data-voyado-facets-list]');
    this.chips = host.querySelector('[data-voyado-chips]');
    this.filtersCol = host.querySelector('[data-voyado-filters-col]');
    this.count = host.querySelector('[data-voyado-count]');
    this.filterCount = host.querySelector('[data-voyado-filter-count]');
    // Search header total (outside the host) — synced to the Voyado total (SSR diverges on autocorrect; null on collections).
    this.headerCount = document.querySelector('[data-voyado-header-count]');
    this.sortOptions = host.querySelector('[data-voyado-sort-options]');
    this.sortLabel = host.querySelector('[data-voyado-sort-label]');
    this.sortDetails = host.querySelector('[data-plp-filter-bar-sort-by]');
    this.applyBtn = host.querySelector('[data-plp-filters-drawer-apply]');
    this.announcer = host.querySelector('[data-plp-filters-drawer-announcer]');
    this.drawerClear = host.querySelector('[data-voyado-drawer-clear]');
    // Header Size pills live outside this host — collection state only.
    this.headerSizePills = document.querySelector('[data-voyado-header-size-pills]');

    // Disruptive promo banners — parsed once from an inert <template>, cloned into the grid on landing (see placePromos).
    this.promos = [];
    const promoTpl = host.querySelector('template[data-voyado-promos]');
    if (promoTpl) {
      this.promos = Array.prototype.slice
        .call(promoTpl.content.querySelectorAll('[data-voyado-promo]'))
        .map(function (n) {
          return {
            index: parseInt(n.getAttribute('data-promo-index'), 10),
            type: n.getAttribute('data-promo-type'),
            position: n.getAttribute('data-promo-position'),
            node: n,
          };
        })
        .filter(function (p) {
          return p.index >= 0;
        })
        .sort(function (a, b) {
          return a.index - b.index;
        });
      // One promo per row-start index: bump any promo whose index is already taken to the next 4-col row.
      // plp-voyado-promos.liquid already offsets each promo's index for the card cells consumed by promos
      // above it (incl. the large's 2×2 span), so no extra row is reserved here.
      const taken = {};
      this.promos.forEach(function (p) {
        while (taken[p.index]) {
          p.index += 4;
        }
        taken[p.index] = true;
      });
    }
  };

  Controller.prototype.bind = function () {
    const self = this;
    // Own the AbortController so destroy() removes every listener, incl. document/window ones.
    this._abort = new AbortController();
    const opt = { signal: this._abort.signal };

    // "View N Items" no longer commits — facets auto-apply; the close flushes any pending.
    if (this.sortOptions) {
      this.sortOptions.addEventListener(
        'change',
        function (e) {
          const input = e.target.closest('input[name="sort_by"]');
          if (input) self.setSort(input.value);
        },
        opt,
      );
    }
    // Clear-all is injected, so delegate from the col + drawer header.
    document.addEventListener(
      'click',
      function (e) {
        if (e.target.closest('[data-voyado-clear-all]')) {
          e.preventDefault();
          self.clearAll();
        }
      },
      opt,
    );
    // Header Size pills are injected; delegate from the persistent host to catch re-renders.
    if (self.headerSizePills) {
      self.headerSizePills.addEventListener(
        'click',
        function (e) {
          const el = e.target.closest('[data-voyado-header-size]');
          if (el && window.VoyadoHeaderSize) {
            e.preventDefault();
            window.VoyadoHeaderSize.apply(self, el.getAttribute('data-voyado-header-size'));
          }
        },
        opt,
      );
    }

    // Rebuild the drawer rows from committed on OPEN (before fade-in) + restore the
    // count; doing it on close re-opened the accordions mid-animation.
    document.addEventListener(
      'voyado:drawer-open',
      function () {
        self.pending = clone(self.committed);
        self.buildFacets(self.facetsData);
        self.renderDrawerClear();
        if (self.committedTotal != null) self.setApplyText(self.committedTotal);
      },
      opt,
    );
    document.addEventListener(
      'voyado:drawer-close',
      function () {
        self.flushApply(); // commit any pending debounced selection before closing
      },
      opt,
    );

    // Persist page count + scroll so back/forward restores the shopper's place.
    window.addEventListener(
      'pagehide',
      function () {
        saveRestore(plpRestoreKey(), { count: self.loaded.length, scroll: window.scrollY });
      },
      opt,
    );

    // Back/forward (soft popstate): re-read the URL + full re-render (facet rows must reflect it).
    window.addEventListener(
      'popstate',
      function () {
        self.committed = readFacetsFromUrl();
        self.pending = clone(self.committed);
        self.sort = new URLSearchParams(location.search).get('sort') || null;
        self.load(true);
      },
      opt,
    );

    // Re-place promos on a 1024px cross: desktop puts a right-positioned banner
    // DOM-before its paired products, mobile after them. Fires once per cross.
    LG.addEventListener(
      'change',
      function () {
        self.placePromos();
      },
      opt,
    );
  };

  // --- Fetch + render ------------------------------------------------------

  Controller.prototype.params = function (reset) {
    return Object.assign(
      {
        skip: reset ? 0 : this.loaded.length,
        // Back/forward: the first reset re-fetches the whole saved page count at once
        // so the grid + scroll restore (see render). Else normal page.
        limit: reset && this.restore ? this.restore.count : PAGE_SIZE,
        sort: this.sort,
      },
      facetParams(this.committed),
    );
  };

  // Set the host state. A static aria-live region toggled from display:none doesn't
  // reliably announce, so for empty/error clear + re-insert its text next frame.
  Controller.prototype.setState = function (state) {
    this.host.setAttribute('data-voyado-state', state);
    const el =
      state === 'empty'
        ? this.host.querySelector('[data-voyado-empty]')
        : state === 'error'
          ? this.host.querySelector('[data-voyado-error]')
          : null;
    if (el && el.textContent) {
      const text = el.textContent;
      el.textContent = '';
      requestAnimationFrame(function () {
        el.textContent = text;
      });
    }
  };

  // A dropped request otherwise leaves the grid saying "no products". Retry twice with a widening gap;
  // the state stays 'loading' throughout, so the shopper sees the spinner rather than a wrong answer.
  const LOAD_RETRIES = 2;
  const LOAD_RETRY_MS = 600;

  Controller.prototype.load = function (reset, keepFacets, attempt) {
    const self = this;
    const gen = ++this._gen;
    const tries = attempt || 0;
    if (reset) this.loaded = [];
    this.setState('loading');
    return this.fetch(this.params(reset))
      .then(function (res) {
        if (gen === self._gen) self.render(res, reset, keepFacets);
      })
      .catch(function () {
        if (gen !== self._gen) return; // superseded by a newer load
        if (tries < LOAD_RETRIES) {
          // Chain the retry INTO the returned promise — the caller's busy latch must not
          // release while a retry is still pending (see voyado-pagination.js).
          return new Promise(function (resolve) {
            setTimeout(resolve, LOAD_RETRY_MS * (tries + 1));
          }).then(function () {
            return gen === self._gen ? self.load(reset, keepFacets, tries + 1) : undefined;
          });
        }
        // Out of retries with products already shown: a page that never arrived. Keep them and retire the
        // sentinel — the empty message would be false here, and its spinner would never stop.
        if (self.loaded.length) {
          self.setState('results');
          const sentinel = self.host.querySelector('[data-voyado-load-more]');
          if (sentinel) sentinel.remove();
          if (self.paginator) {
            self.paginator.destroy();
            self.paginator = null;
          }
          return;
        }
        self.setState(self.errorState);
      });
  };

  Controller.prototype.render = function (res, reset, keepFacets) {
    const list = res && res.primaryList;
    if (!list) {
      this.setState('error');
      return;
    }

    if (reset) {
      // Applied autocorrection (search only): results are already for the corrected
      // phrase, so just reflect the term in the UI — no re-query.
      if (this.onAutoCorrect && res.autoCorrect && res.autoCorrect.q) {
        this.onAutoCorrect(res.autoCorrect.q, res.autoCorrect.originalTotalHits);
      }
      this.grid.replaceChildren();
      this.facetsData = list.facets || [];
      // An unfiltered response IS the facet universe; a deep-linked filtered first
      // response is already reduced, so seed the universe from a separate query.
      if (Object.keys(this.committed).length === 0) this.facetUniverse = this.facetsData;
      else if (!this.facetUniverse && !this._seeding) {
        this._seeding = true;
        this.seedUniverse();
      }
      // keepFacets: refresh counts in place (buildFacets would rebuild + drop drawer state).
      if (keepFacets) this.updateFacetCounts(this.facetsData);
      else this.buildFacets(this.facetsData);
      this.buildChips(this.facetsData);
      if (window.VoyadoHeaderSize) window.VoyadoHeaderSize.build(this);
      this.buildSort(list.sort);
      this.loaded = [];
    }

    const groups = list.productGroups || [];
    const grid = this.grid;
    const mains = [];
    let primed = false;
    groups.forEach(function (g) {
      const ps = (g && g.products) || [];
      if (!ps[0]) return;
      mains.push(ps[0]);
      // Pass the whole colour group (siblings) for swatches + hover-swap. The first
      // card of a reset render holds the LCP image → prioritise it.
      grid.appendChild(window.renderVoyadoCard(ps[0], { quickAdd: true, siblings: ps, priority: reset && !primed }));
      primed = true;
    });
    this.loaded = this.loaded.concat(mains);

    // Landing only: drop the disruptive promos in. Skipped on filtered/sorted
    // re-renders and on load-more (pagination is not a reset).
    if (reset && this.promos.length && this.isLanding()) this.placePromos();

    this.committedTotal = list.totalHits; // remembered so a drawer-open discard can restore the Apply text
    this.setCount(list.totalHits);
    this.setApplyText(list.totalHits);
    this.setState(this.loaded.length === 0 ? 'empty' : 'results');
    this.ensurePagination(list.totalHits);

    // Back/forward: the first reset re-fetched the saved card count; jump to where
    // the shopper was. Cards reserve height, so the scroll target is stable.
    if (reset && this.restore) {
      window.scrollTo(0, this.restore.scroll);
      this.restore = null; // one-shot — later resets (filter/sort) paginate normally
    }

    document.dispatchEvent(new CustomEvent('voyado:cards-rendered', { detail: { root: this.grid } }));

    // Surface the full response so the search secondary-list can react without a
    // duplicate query; its listener gates on a single-page totalHits.
    document.dispatchEvent(
      new CustomEvent('voyado:plp-rendered', {
        detail: { response: res, reset: reset, pageSize: PAGE_SIZE },
      }),
    );
  };

  // Landing = no committed facets and unset sort. Promos only show here.
  Controller.prototype.isLanding = function () {
    return Object.keys(this.committed).length === 0 && !this.sort;
  };

  // Clone each promo node and insert it before the Nth product card; a promo whose
  // index exceeds the card count is skipped (collection too short for the slot).
  Controller.prototype.placePromos = function () {
    const grid = this.grid;
    // Idempotent: clear prior clones so a breakpoint cross doesn't duplicate banners.
    grid.querySelectorAll('[data-voyado-promo]').forEach(function (n) {
      n.remove();
    });
    if (!this.promos.length || !this.isLanding()) return;
    const isDesktop = LG.matches;
    this.promos.forEach(function (p) {
      const cards = grid.querySelectorAll('[data-voyado-card]');
      if (p.index >= cards.length) return; // grid too short for this slot — skip
      // A right-positioned double/large banner sits RIGHT of its 2 paired products on
      // desktop; on mobile/tablet (single flow) drop it BELOW those 2 instead.
      let at = p.index;
      if (!isDesktop && p.position === 'right' && (p.type === 'double' || p.type === 'large')) {
        at = p.index + 2;
      }
      grid.insertBefore(p.node.cloneNode(true), cards[at] || null); // cards[at] undefined → append
    });
  };

  // --- Facet universe (show every value, even zeroed) -----------------------
  // Voyado drops a value at 0 products; we remember the full set (the "universe") and
  // overlay live counts + selection onto it so every option stays visible with a "(0)".

  function facetValues(facet) {
    if (!facet) return [];
    if (facet.type === 'SIZE') {
      // Flatten ALL sizeTypes/formats (not just [0]) — keeps every size visible; each
      // value keeps its own id so filtering/round-trip stay correct (see voyado-facet-renderer).
      return (facet.sizeTypes || []).reduce(function (acc, st) {
        (st.formats || []).forEach(function (fmt) {
          if (fmt.values) acc.push.apply(acc, fmt.values);
        });
        return acc;
      }, []);
    }
    return facet.values || [];
  }

  // Universe facet `uf` overlaid with the live counts/selection from `cf`. Missing
  // values become count 0, unselected. CHECKBOX has none to merge; RANGE via overlayRange.
  function overlayFacet(uf, cf) {
    if (uf.type === 'CHECKBOX') return cf || uf;
    const live = {};
    facetValues(cf).forEach(function (v) {
      live[v.id] = v;
    });
    const merge = function (uv) {
      const cv = live[uv.id];
      return Object.assign({}, uv, {
        count: cv && cv.count != null ? cv.count : 0,
        selected: cv ? !!cv.selected : false,
      });
    };
    if (uf.type === 'SIZE') {
      // Rebuild ALL sizeTypes/formats (not just [0]) — overlay live counts/selection on
      // every value so no size system is dropped from the universe.
      return Object.assign({}, uf, {
        sizeTypes: (uf.sizeTypes || []).map(function (st) {
          return Object.assign({}, st, {
            formats: (st.formats || []).map(function (fmt) {
              return Object.assign({}, fmt, { values: (fmt.values || []).map(merge) });
            }),
          });
        }),
      });
    }
    return Object.assign({}, uf, { values: (uf.values || []).map(merge) });
  }

  // Unfiltered query for the full value set — best-effort; a failure just leaves
  // the drawer showing the live (reduced) facets.
  Controller.prototype.seedUniverse = function () {
    const self = this;
    this.fetch({ skip: 0, limit: 1, sort: this.sort })
      .then(function (res) {
        const list = res && res.primaryList;
        if (list && list.facets && list.facets.length) {
          self.facetUniverse = list.facets;
          if (self.facetsData && self.facetsData.length) self.buildFacets(self.facetsData);
        }
      })
      .catch(function () {});
  };

  // The universe (full value set) overlaid with the live response's counts and
  // selection. Falls back to the live facets until the universe is known.
  Controller.prototype.mergedFacets = function (current) {
    const universe = this.facetUniverse;
    if (!universe || !universe.length) return current || [];
    const self = this;
    const live = {};
    (current || []).forEach(function (f) {
      live[f.id] = f;
    });
    const out = universe.map(function (uf) {
      return uf.type === 'RANGE' ? self.overlayRange(uf) : overlayFacet(uf, live[uf.id]);
    });
    // Defensive: surface any live facet missing from the universe (partial seed).
    (current || []).forEach(function (f) {
      if (
        !universe.some(function (u) {
          return u.id === f.id;
        })
      )
        out.push(f);
    });
    return out;
  };

  // RANGE rebuilt with the full universe min/max plus the committed selection, so
  // the slider spans the original range with thumbs at the chosen bounds.
  Controller.prototype.overlayRange = function (uf) {
    const sel = this.committed[uf.id];
    return Object.assign({}, uf, {
      minSelected: sel && sel.min != null ? sel.min : uf.min,
      maxSelected: sel && sel.max != null ? sel.max : uf.max,
    });
  };

  Controller.prototype.buildFacets = function (facets) {
    const self = this;
    const frag = document.createDocumentFragment();
    this.mergedFacets(facets).forEach(function (f) {
      frag.appendChild(
        window.renderVoyadoFacet(f, {
          onChange: function (id, vid, checked) {
            self.onFacetChange(id, vid, checked);
          },
          onRange: function (id, min, max) {
            self.onRange(id, min, max);
          },
        }),
      );
    });
    this.facetsList.replaceChildren(frag);
    if (window.initVoyadoPriceRange) window.initVoyadoPriceRange(this.facetsList);
    this.hideEmptyFacets();
  };

  // Refresh per-option counts in place from a preview response — no DOM rebuild, so
  // focus / accordion state / scroll are kept. Matches by [data-filter-param] + input value.
  Controller.prototype.updateFacetCounts = function (facets) {
    const listEl = this.facetsList;
    if (!listEl || !facets) return;
    const byId = {};
    facets.forEach(function (f) {
      byId[f.id] = f;
    });
    listEl.querySelectorAll('[data-filter-param]').forEach(function (acc) {
      // CHECKBOX has no per-option count — skip (type read off the DOM, set at render).
      if (acc.getAttribute('data-facet-type') === 'CHECKBOX') return;
      // `f` is absent when Voyado drops a sparse facet; fall through with an empty
      // value set so every row reads (0) rather than keeping a stale count.
      const f = byId[acc.getAttribute('data-filter-param')];
      let values = [];
      if (f) {
        if (f.type === 'SIZE') {
          // Flatten ALL sizeTypes/formats (not just [0]) — every size's count updates.
          values = (f.sizeTypes || []).reduce(function (acc, st) {
            (st.formats || []).forEach(function (fmt) {
              if (fmt.values) acc.push.apply(acc, fmt.values);
            });
            return acc;
          }, []);
        } else {
          values = f.values || [];
        }
      }
      const countById = {};
      values.forEach(function (v) {
        countById[v.id] = v.count;
      });
      acc.querySelectorAll('li input[data-slot="input"]').forEach(function (input) {
        const li = input.closest('li');
        const el = li && (li.querySelector('[data-slot="text"]') || li.querySelector('[data-slot="label"]'));
        if (!el) return;
        const base = el.dataset.label != null ? el.dataset.label : el.textContent;
        const c = countById[input.value];
        window.setVoyadoLabelWithCount(el, base, c != null ? c : 0);
        window.setVoyadoRowVisible(li, c != null ? c : 0, input.checked);
      });
      // Re-collapse the grid to the now-visible rows so 0-count options leave no reserved tracks/gaps.
      if (window.setVoyadoListLayout)
        acc.querySelectorAll('ul').forEach(function (ul) {
          window.setVoyadoListLayout(ul);
        });
    });
    this.hideEmptyFacets();
  };

  // Hide an accordion when all its rows are hidden (RANGE/CHECKBOX have no rows, skipped).
  Controller.prototype.hideEmptyFacets = function () {
    if (!this.facetsList) return;
    this.facetsList.querySelectorAll('[data-filter-param]').forEach(function (acc) {
      const rows = acc.querySelectorAll('li');
      if (!rows.length) return;
      let anyVisible = false;
      rows.forEach(function (li) {
        if (li.style.display !== 'none') anyVisible = true;
      });
      acc.style.display = anyVisible ? '' : 'none';
    });
  };

  Controller.prototype.buildChips = function (facets) {
    const frag = document.createDocumentFragment();
    // At most BAR_CHIP_LIMIT chips in the bar (RANGE never gets one); every facet
    // stays reachable via the drawer.
    facets
      .filter(function (f) {
        return f.type !== 'RANGE';
      })
      .slice(0, BAR_CHIP_LIMIT)
      .forEach(function (f) {
        const chip = window.voyadoTmpl('chip');
        if (!chip) return;
        chip.setAttribute('data-plp-filter-target', f.id);
        window.voyadoSlot(chip, 'label').textContent = f.label;
        frag.appendChild(chip);
      });
    this.chips.replaceChildren(frag);
    // Bar label count — "Filters (N)" on mobile/tablet; desktop shows the chips instead.
    if (this.filterCount) {
      const applied = this.activeCount(this.committed);
      this.filterCount.textContent = applied > 0 ? ' (' + applied + ')' : '';
    }
    this.renderClearAll();
  };

  Controller.prototype.buildSort = function (sort) {
    if (!this.sortOptions || !sort) return;
    const opts = sort.options || [];
    const selected = this.sort || sort.selected || (opts[0] && opts[0].id);
    const frag = document.createDocumentFragment();
    const self = this;
    opts.forEach(function (o) {
      const row = window.voyadoTmpl('sort-option');
      if (!row) return;
      const input = window.voyadoSlot(row, 'input');
      input.value = o.id;
      if (o.id === selected) input.checked = true;
      window.voyadoSlot(row, 'text').textContent = o.label;
      frag.appendChild(row);
      if (o.id === selected && self.sortLabel) self.sortLabel.textContent = o.label;
    });
    this.sortOptions.replaceChildren(frag);
  };

  Controller.prototype.renderClearAll = function () {
    // Bar clear-all reflects the applied (committed) filters.
    const n = this.activeCount(this.committed);
    if (this.filtersColClear && this.filtersColClear.parentNode)
      this.filtersColClear.parentNode.removeChild(this.filtersColClear);
    this.filtersColClear = null;
    if (this.filtersCol && n > 0) {
      const c = window.voyadoTmpl('clear');
      window.voyadoSlot(c, 'label').textContent = 'Clear all (' + n + ')';
      this.filtersCol.appendChild(c);
      this.filtersColClear = c;
    }
    this.renderDrawerClear();
  };

  // Drawer-heading clear-all reflects the live (pending) selection, updating the
  // moment a filter is ticked — before Apply commits.
  Controller.prototype.renderDrawerClear = function () {
    if (!this.drawerClear) return;
    const n = this.activeCount(this.pending);
    if (n === 0) {
      this.drawerClear.replaceChildren();
      this.drawerClearNode = null;
      return;
    }
    const d = window.voyadoTmpl('drawer-clear');
    window.voyadoSlot(d, 'label').textContent = 'Clear all (' + n + ')';
    this.drawerClear.replaceChildren(d);
    this.drawerClearNode = d;
  };

  // --- Interaction ---------------------------------------------------------

  Controller.prototype.onFacetChange = function (id, vid, checked) {
    const arr = this.pending[id] || [];
    this.pending[id] = checked
      ? arr.concat([vid])
      : arr.filter(function (v) {
          return v !== vid;
        });
    if (!this.pending[id].length) delete this.pending[id];
    this.renderDrawerClear();
    this.scheduleAutoApply();
  };

  Controller.prototype.onRange = function (id, min, max) {
    this.pending[id] = { min: min, max: max };
    this.scheduleAutoApply();
  };

  Controller.prototype.scheduleAutoApply = function () {
    const self = this;
    clearTimeout(this.applyTimer);
    this.applyTimer = setTimeout(function () {
      self.autoApply();
    }, 300);
  };

  // Commit the pending selection and re-render the grid only (see render(keepFacets)).
  Controller.prototype.autoApply = function () {
    clearTimeout(this.applyTimer);
    this.applyTimer = null;
    const wasEmpty = Object.keys(this.committed).length === 0;
    this.committed = clone(this.pending);
    // Push once on entering a filtered state (Back → unfiltered), replace after.
    const enteringFiltered = wasEmpty && Object.keys(this.committed).length > 0;
    this.syncUrl(!enteringFiltered);
    this.load(true, true); // reset + keepFacets
  };

  // Flush a pending debounced auto-apply now (e.g. the drawer is closing).
  Controller.prototype.flushApply = function () {
    if (this.applyTimer) this.autoApply();
  };

  Controller.prototype.setSort = function (value) {
    this.sort = value || null;
    const checked = this.sortOptions.querySelector('input[name="sort_by"]:checked');
    if (this.sortLabel && checked) {
      const text = checked.parentNode.querySelector('[data-slot="text"]');
      if (text) this.sortLabel.textContent = text.textContent;
    }
    if (this.sortDetails) this.sortDetails.open = false;
    this.syncUrl();
    this.load(true);
  };

  Controller.prototype.clearAll = function () {
    this.committed = {};
    this.pending = {};
    this.syncUrl();
    this.load(true);
  };

  // --- Helpers -------------------------------------------------------------

  Controller.prototype.activeCount = function (facets) {
    let n = 0;
    Object.keys(facets).forEach(function (id) {
      const v = facets[id];
      if (Array.isArray(v)) n += v.length; // range objects excluded (mirrors original)
    });
    return n;
  };

  Controller.prototype.setCount = function (total) {
    const label = total + (total === 1 ? ' item' : ' items');
    if (this.count) this.count.textContent = label;
    if (this.headerCount) this.headerCount.textContent = label;
  };

  Controller.prototype.setApplyText = function (total, announce) {
    if (!this.applyBtn) return;
    const text = total === 0 ? 'No Results' : 'View ' + total + ' ' + (total === 1 ? 'Item' : 'Items');
    const el = this.applyBtn.querySelector('.action-pill__text');
    if (el) el.textContent = text;
    // Announce only on user-initiated preview, not the initial/page-load count.
    if (announce && this.announcer) this.announcer.textContent = text;
  };

  Controller.prototype.ensurePagination = function (total) {
    // QA-only: `?plp_loader=1` pins the load-more spinner (no observer) so it can be
    // inspected without network throttling. Inert without the param.
    if (new URLSearchParams(location.search).has('plp_loader')) {
      if (!this.host.querySelector('[data-voyado-load-more]')) {
        const dbgSentinel = window.voyadoTmpl('load-more');
        if (dbgSentinel) this.grid.after(dbgSentinel);
      }
      return;
    }
    const existing = this.host.querySelector('[data-voyado-load-more]');
    // All pages loaded: drop the sentinel + observer so it stops firing.
    if (this.loaded.length >= total) {
      if (existing) existing.remove();
      if (this.paginator) {
        this.paginator.destroy();
        this.paginator = null;
      }
      return;
    }
    if (existing) return;
    const sentinel = window.voyadoTmpl('load-more'); // spinner + sr-only announcer
    if (!sentinel) return;
    this.grid.after(sentinel);
    const self = this;
    if (this.paginator) this.paginator.destroy();
    this.paginator = window.createVoyadoPagination({
      root: this.host,
      sentinelSelector: '[data-voyado-load-more]',
      onLoadMore: function () {
        const ann = self.host.querySelector('[data-voyado-load-announce]');
        if (ann) ann.textContent = 'Loading more products'; // TODO(i18n)
        return self.load(false); // the paginator's busy latch + re-check wait on this promise
      },
    });
  };

  Controller.prototype.syncUrl = function (replace) {
    const params = new URLSearchParams(location.search);
    // Strip prior f.* + sort, then re-write from committed.
    Array.from(params.keys()).forEach(function (k) {
      if (k === 'sort' || k.indexOf('f.') === 0) params.delete(k);
    });
    const fp = facetParams(this.committed);
    Object.keys(fp).forEach(function (k) {
      params.set(k, fp[k]);
    });
    if (this.sort) params.set('sort', this.sort);
    const qs = params.toString();
    const url = location.pathname + (qs ? '?' + qs : '');
    history[replace ? 'replaceState' : 'pushState']({}, '', url);
  };

  Controller.prototype.destroy = function () {
    clearTimeout(this.applyTimer);
    if (this.paginator) this.paginator.destroy();
    if (this._abort) this._abort.abort();
  };

  // --- Module helpers ------------------------------------------------------

  function clone(o) {
    return JSON.parse(JSON.stringify(o));
  }

  window.VoyadoPlp = {
    attach: function (host, opts) {
      return new Controller(host, opts);
    },
  };
})();
